// 📁 hooks/useFullPageScroll.ts
import { useState, useRef, useEffect, useCallback } from "react";
import { useResponsive } from "./useResponsive";

type UseFullPageScrollProps = {
  sectionsCount?: number;
  duration?: number;
};

export function useFullPageScroll({
  sectionsCount = 1,
  duration = 800,
}: UseFullPageScrollProps) {
  const { isDesktop } = useResponsive();
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const isLocked = useRef<boolean>(false);
  const animationTimer = useRef<NodeJS.Timeout | null>(null);
  const wheelQuietTimer = useRef<NodeJS.Timeout | null>(null);
  const activeIndexRef = useRef<number>(activeIndex);

  // Sync ref with state for event listeners
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const scrollToSection = useCallback(
    (index: number) => {
      if (!isDesktop) return;
      if (index < 0 || index >= sectionsCount) return;

      isLocked.current = true;
      setActiveIndex(index);
      activeIndexRef.current = index;

      if (animationTimer.current) {
        clearTimeout(animationTimer.current);
      }

      animationTimer.current = setTimeout(() => {
        animationTimer.current = null;
        // Only unlock if wheel momentum has completely stopped
        if (!wheelQuietTimer.current) {
          isLocked.current = false;
        }
      }, duration);
    },
    [sectionsCount, isDesktop, duration],
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!isDesktop) return;

      // Do NOT intercept wheel or switch sections if scroll is locked by a modal/popup
      if (
        document.body.getAttribute("data-scroll-locked") === "true" ||
        document.body.style.overflow === "hidden"
      ) {
        return;
      }

      // Prevent native browser scroll when full page mode is enabled
      e.preventDefault();

      // Reset quiet timer on every wheel event (detects continuous touchpad momentum)
      if (wheelQuietTimer.current) {
        clearTimeout(wheelQuietTimer.current);
      }

      wheelQuietTimer.current = setTimeout(() => {
        wheelQuietTimer.current = null;
        // If slide animation has finished, unlock for the next user swipe
        if (!animationTimer.current) {
          isLocked.current = false;
        }
      }, 150);

      // Ignore if locked or if delta is minor drift (< 12px)
      if (isLocked.current || Math.abs(e.deltaY) < 12) {
        return;
      }

      const currentIndex = activeIndexRef.current;
      if (e.deltaY > 0) {
        if (currentIndex < sectionsCount - 1) {
          scrollToSection(currentIndex + 1);
        }
      } else {
        if (currentIndex > 0) {
          scrollToSection(currentIndex - 1);
        }
      }
    },
    [sectionsCount, scrollToSection, isDesktop],
  );

  // Keyboard navigation handler (ArrowDown, ArrowUp, PageDown, PageUp)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        !isDesktop ||
        isLocked.current ||
        document.body.getAttribute("data-scroll-locked") === "true" ||
        document.body.style.overflow === "hidden"
      ) {
        return;
      }

      const currentIndex = activeIndexRef.current;
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        if (currentIndex < sectionsCount - 1) {
          e.preventDefault();
          scrollToSection(currentIndex + 1);
        }
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        if (currentIndex > 0) {
          e.preventDefault();
          scrollToSection(currentIndex - 1);
        }
      }
    },
    [isDesktop, sectionsCount, scrollToSection],
  );

  useEffect(() => {
    if (!isDesktop) return;

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
      if (animationTimer.current) clearTimeout(animationTimer.current);
      if (wheelQuietTimer.current) clearTimeout(wheelQuietTimer.current);
    };
  }, [handleWheel, handleKeyDown, isDesktop]);

  return { activeIndex, scrollToSection, isDesktop };
}
